import { IntlShape } from 'react-intl'
import { UrgencyWaitCauseDTO } from '../../dto/urgencySummary/UrgencySummaryDTO'
import { formatDecimal, formatInt } from '../ServicesStatistics/statisticsUtils'
import { causeText as statisticsCauseText, isCriticalPriorityName } from '../ServicesStatistics/waitCauseText'
import { crossesMidnight } from './urgencyAnalysis'

export { MAX_CAUSES_SHOWN } from '../ServicesStatistics/waitCauseText'

/**
 * Las posibles causas de la espera de una guardia escritas en palabras. Es la misma redacción que usa el PDF
 * (`UrgencyWaitCauseTexts.java`): si cambia una, cambiar la otra. Con una diferencia: acá se cuida el singular («1 paciente»)
 * y la franja que cruza la medianoche («entre las 22 h y las 2 h del día siguiente»), que el PDF dice como «entre las 22 y
 * las 2 h». Son hipótesis a partir de los datos, no un diagnóstico.
 */

const numberParam = (cause: UrgencyWaitCauseDTO, key: string): number => {
  const value = Number(cause.params[key])
  return Number.isFinite(value) ? value : 0
}

/** «entre las 13 y las 18 h» o, si cruza la medianoche, «entre las 22 h y las 2 h del día siguiente». `toHour` va de 1 a 24. */
export function hourRangeText(intl: IntlShape, fromHour: number, toHour: number): string {
  return intl.formatMessage(
    { id: 'UrgencySummaryHourRange' },
    { from: fromHour, to: toHour, crosses: crossesMidnight(fromHour, toHour) ? 'true' : 'false' },
  )
}

/** La causa es la espera por encima del objetivo de un código de Inmediato o Emergencia: va resaltada. */
export const isCriticalUrgencyCause = (cause: UrgencyWaitCauseDTO): boolean =>
  cause.code === 'PRIORITY_WAIT' && isCriticalPriorityName(String(cause.params.priorityName ?? ''))

/** La causa escrita en palabras, con el número que importa y la primera letra en mayúscula. */
export function urgencyCauseText(intl: IntlShape, cause: UrgencyWaitCauseDTO): string {
  const n = (key: string) => numberParam(cause, key)
  const range = () => hourRangeText(intl, n('fromHour'), n('toHour'))
  switch (cause.code) {
    case 'PRIORITY_WAIT':
      // Mismo texto que en Estadísticas de atención
      return statisticsCauseText(intl, { code: 'PRIORITY_WAIT', params: cause.params })
    case 'PEAK_HOURS':
      return capitalize(intl.formatMessage({ id: 'UrgencySummaryCausePeakHours' }, { range: range(), sharePct: formatInt(n('sharePct')) }))
    case 'NO_TRIAGE':
      return capitalize(intl.formatMessage({ id: 'UrgencySummaryCauseNoTriage' }, { pct: formatInt(n('pct')), count: n('count'), countText: formatInt(n('count')) }))
    case 'LEFT_WITHOUT_ATTENTION':
      return capitalize(intl.formatMessage(
        { id: 'UrgencySummaryCauseLeft' },
        { count: n('count'), countText: formatInt(n('count')), pct: formatDecimal(n('pct')), range: range() },
      ))
  }
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
